import React, { useState, useEffect, useRef } from "react";
import { useForm, ValidationError } from '@formspree/react';
import styles from "./Contact.module.css";
import { FaMapMarkerAlt, FaPhone } from "react-icons/fa";
import { FiTwitter, FiInstagram, FiFacebook, FiLinkedin } from "react-icons/fi";

const Contact = () => {
  const [state, handleSubmit] = useForm("xblypjlp");

  const [isVisible, setIsVisible] = useState({
    hero: false,
    heading: false,
    contactInfo: false,
    contactForm: false
  });
  
  const heroRef = useRef(null);
  const sectionRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target === heroRef.current) {
            setIsVisible(prev => ({ ...prev, hero: true }));
          } else if (entry.target === sectionRef.current) {
            setTimeout(() => setIsVisible(prev => ({ ...prev, heading: true })), 100);
            setTimeout(() => setIsVisible(prev => ({ ...prev, contactInfo: true })), 300);
            setTimeout(() => setIsVisible(prev => ({ ...prev, contactForm: true })), 500);
          }
          
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    const currentHeroRef = heroRef.current;
    const currentSectionRef = sectionRef.current;
    
    if (currentHeroRef) observer.observe(currentHeroRef);
    if (currentSectionRef) observer.observe(currentSectionRef);
    
    return () => {
      if (currentHeroRef) observer.unobserve(currentHeroRef);
      if (currentSectionRef) observer.unobserve(currentSectionRef);
    };
  }, []);

  return (
    <div className={styles.contactPage}>
      {/* Hero Section */}
      <section className={styles.heroSection} ref={heroRef}>
        <div className={styles.heroOverlay}></div>
        <div className={`${styles.heroContent} ${isVisible.hero ? styles.visible : ""}`}>
          <h1 className={styles.heroTitle}>Get In Touch</h1>
          <p className={styles.heroSubtitle}>We'd love to hear from you. Let's start a conversation.</p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className={styles.contactSection} ref={sectionRef}>
        <div className={styles.container}>
          <div
            className={`${styles.heading} ${isVisible.heading ? styles.visible : ""}`}
          >
            <span className={styles.sectionLabel}>Contact Us</span>
            <h2 className={styles.title}>How Can We Help?</h2>
            <div className={styles.underline}></div>
            <p className={styles.subtitle}>Whether you have a question about our services, pricing, or anything else, our team is ready to answer all your questions.</p>
          </div>

          <div className={styles.contactContent}>
            <div
              className={`${styles.contactInfo} ${isVisible.contactInfo ? styles.visible : ""}`}
            >
              <div className={styles.infoCards}>
                <div className={styles.infoCard}>
                  <div className={styles.infoIcon}>
                    <FaPhone />
                  </div>
                  <h3 className={styles.infoTitle}>Phone</h3>
                  <p className={styles.infoText}>
                    <a href="tel:+16045622408">(604) 562-2408</a>
                  </p>
                </div>
                
                <div className={styles.infoCard}>
                  <div className={styles.infoIcon}>
                    <FaMapMarkerAlt />
                  </div>
                  <h3 className={styles.infoTitle}>Office</h3>
                  <p className={styles.infoText}>
                    We're located in Vancouver, BC
                  </p>
                </div>
                
                <div className={styles.socialContainer}>
                  <h3 className={styles.socialTitle}>Connect With Us</h3>
                  <div className={styles.socialLinks}>
                    <a href="https://facebook.com" className={styles.socialIcon} aria-label="Facebook">
                      <FiFacebook />
                    </a>
                    <a href="https://twitter.com" className={styles.socialIcon} aria-label="Twitter">
                      <FiTwitter />
                    </a>
                    <a href="https://instagram.com" className={styles.socialIcon} aria-label="Instagram">
                      <FiInstagram />
                    </a>
                    <a href="https://linkedin.com" className={styles.socialIcon} aria-label="LinkedIn">
                      <FiLinkedin />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`${styles.contactForm} ${isVisible.contactForm ? styles.visible : ""}`}
            >
              <div className={styles.formCard}>
                <h3 className={styles.formTitle}>Send us a message</h3>
                
                {state.succeeded ? (
                  <div className={styles.successMessage}>
                    <div className={styles.successIcon}>✓</div>
                    <h3>Thanks for your message!</h3>
                    <p>We appreciate you contacting us and will respond shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className={styles.formContent}>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label htmlFor="name">Your Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          placeholder="John Doe"
                          required
                        />
                        <ValidationError 
                          prefix="Name" 
                          field="name"
                          errors={state.errors}
                        />
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor="email">Your Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="john@example.com"
                          required
                        />
                        <ValidationError 
                          prefix="Email" 
                          field="email"
                          errors={state.errors}
                        />
                      </div>
                    </div>
                    
                    <div className={styles.formGroup}>
                      <label htmlFor="subject">Subject</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        placeholder="How can we help you?"
                      />
                      <ValidationError 
                        prefix="Subject" 
                        field="subject"
                        errors={state.errors}
                      />
                    </div>
                    
                    <div className={styles.formGroup}>
                      <label htmlFor="message">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        rows="5"
                        placeholder="Your message here..."
                        required
                      ></textarea>
                      <ValidationError 
                        prefix="Message" 
                        field="message"
                        errors={state.errors}
                      />
                    </div>
                    
                    <button type="submit" className={styles.submitButton} disabled={state.submitting}>
                      Send Message
                      <span className={styles.buttonIcon}>→</span>
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.container}>
          <div className={styles.faqHeading}>
            <span className={styles.sectionLabel}>FAQs</span>
            <h2 className={styles.title}>Frequently Asked Questions</h2>
            <div className={styles.underline}></div>
          </div>
          
          <div className={styles.faqGrid}>
            <div className={styles.faqItem}>
              <h3 className={styles.faqQuestion}>What services do you offer?</h3>
              <p className={styles.faqAnswer}>We offer a comprehensive range of digital marketing services including SEO, social media marketing, web development, email marketing, and growth strategy consulting to help businesses succeed online.</p>
            </div>
            
            <div className={styles.faqItem}>
              <h3 className={styles.faqQuestion}>How long does it take to see results?</h3>
              <p className={styles.faqAnswer}>Results vary depending on the service and your specific goals, but most clients start seeing improvements within 30-90 days. SEO typically takes 3-6 months for significant results, while social media and advertising can show results within weeks.</p>
            </div>
            
            <div className={styles.faqItem}>
              <h3 className={styles.faqQuestion}>Do you work with businesses of all sizes?</h3>
              <p className={styles.faqAnswer}>Yes! We work with startups, small businesses, and large enterprises. Our services are tailored to meet the unique needs and budgets of businesses at every stage of growth.</p>
            </div>
            
            <div className={styles.faqItem}>
              <h3 className={styles.faqQuestion}>How do we get started working together?</h3>
              <p className={styles.faqAnswer}>Getting started is easy! Simply fill out our contact form, and one of our team members will reach out to schedule a consultation. During this initial meeting, we'll discuss your goals and how we can help you achieve them.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 